import { useEffect } from "react";
import { useFormik } from "formik";
import { PublicKey } from "@solana/web3.js";
import { useWallet } from "@solana/wallet-adapter-react";
import { AssetConfig } from "../../../config/assets";
import { useBalance } from "../../../hooks/useBalance";
import { useMintTo } from "../../../hooks/useMintTo";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { useErrorToast, useSuccessToast } from "../../../helpers/toast";
import { solAmountFormatter } from "../../../helpers/formatter";
import { StakeFormProps, TokenAddon } from "../StakeForm";
import { useState } from "react";

interface UseRestakeSimpleOptions {
  config: AssetConfig;
  resetKey: string;
}

export const useRestakeSimple = ({ config, resetKey }: UseRestakeSimpleOptions) => {
  const { publicKey } = useWallet();
  const [displayedBalance, setDisplayedBalance] = useState(`0 ${config.underlyingLabel}`);
  const [formDisabled, setFormDisabled] = useState(false);

  const { balance: tokenBalance, fetchBalance: fetchTokenBalance } = useBalance();
  const { errorToast } = useErrorToast();
  const { successToast } = useSuccessToast();
  const { mintTo, error: mintToError } = useMintTo();
  const estimatedRate = 1;
  const { rate, fetchRate } = useExchangeRate();

  const formik = useFormik({
    initialValues: { amount: "", receivedAmount: "" },
    onSubmit: async (values) => {
      try {
        const signature = await mintTo(
          config.vrtMintAddress,
          config.mintAddress,
          config.vaultAddress,
          values.amount,
          undefined
        );
        if (signature) {
          successToast("Your mint was successful!");
          reload();
        }
      } catch (err) {
        errorToast(`${err}`);
      }
    },
  });

  const reload = () => {
    formik.resetForm();
    fetchTokenBalance(new PublicKey(config.mintAddress), publicKey);
    fetchRate(config.vaultAddress);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numberValue = Number(inputValue);
    const outputValue = numberValue * (rate ?? estimatedRate);
    formik.setValues({
      amount: inputValue,
      receivedAmount: solAmountFormatter.format(outputValue),
    });
  };

  const handleMaxClick = () => {
    const maxBalance = tokenBalance?.toString() || "0";
    const maxOutput = Number(tokenBalance ?? "0") * (rate ?? estimatedRate);
    formik.setValues({
      amount: solAmountFormatter.format(Number(maxBalance)).replace(/,/g, ""),
      receivedAmount: solAmountFormatter.format(Number(maxOutput)).replace(/,/g, ""),
    });
  };

  useEffect(() => {
    if (mintToError) {
      errorToast(mintToError);
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mintToError]);

  useEffect(() => {
    formik.resetForm();
    fetchTokenBalance(new PublicKey(config.mintAddress), publicKey);
    fetchRate(config.vaultAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, publicKey]);

  useEffect(() => {
    setFormDisabled(
      Number(formik.values.amount) > Number(tokenBalance?.toString() || "0") ||
        formik.values.amount === "0" ||
        formik.values.amount === ""
    );
  }, [formik.values.amount, tokenBalance]);

  useEffect(() => {
    setDisplayedBalance(
      solAmountFormatter.format(Number(tokenBalance?.toString())) + ` ${config.underlyingLabel}`
    );
  }, [tokenBalance, config.underlyingLabel]);

  const stakeFormProps: StakeFormProps = {
    displayedBalance,
    amount: formik.values.amount,
    receivedAmount: formik.values.receivedAmount,
    onAmountChange: handleInputChange,
    onMax: handleMaxClick,
    inputTokenSelector: <TokenAddon image={config.mintImage} symbol={config.underlyingLabel} />,
    outputTokenSelector: <TokenAddon image={config.vrtImage} symbol={config.vrtName} />,
    receiveHint: "No slippage",
    submitLabel: `Convert to ${config.vrtName}`,
    formDisabled,
    vaultPaused: config.vaultPaused,
    isSubmitting: formik.isSubmitting,
    onSubmit: formik.handleSubmit,
    details:
      rate != null
        ? [
            {
              label: "Rate",
              value: `1 ${config.underlyingLabel} = ${rate.toFixed(5)} ${config.vrtName}`,
            },
          ]
        : [],
  };

  return { stakeFormProps, formik };
};
